/**
 * The Split Test Variants report, rendered as a comparison of cards.
 *
 * The question a reader brings to this report is which arm of a test won. A table
 * answers that by making them read down a column; a card puts one arm's whole set
 * of figures in a single block so two arms can be compared side by side. The cards
 * wrap, so the report has no horizontal axis to manage at narrow widths.
 *
 * Revision grouping above the data is rendered with the same banner the other two
 * reports use, so revision scoping reads identically across all three.
 */

import React from 'react'
import { Flex, Spin, Tag } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { ImpressionsColumnView } from '../../../components/impressions-column-view/impressions-column-view'
import { ClicksColumnView } from '../../../components/clicks-column-view/clicks-column-view'
import { PurchasesColumnView } from '../../../components/purchases-column-view/purchases-column-view'
import { RevenueColumnView } from '../../../components/revenue-column-view/revenue-column-view'
import { numberWithCommas, numberWithPercent } from '../../../_utils/utils'
import { RevisionGroupHeader } from './revision-group-header'
import { isPromotedVariantRow } from './revision-insights'
import type { RevisionMeta, RevisionSubtotals } from './revision-insights'
import { FeatureEmptyState } from '../../../components/icons/feature-icon'

/**
 * One variant's row, in the shape the Insights tab formats every row into. The
 * comparison reads exactly the rows the tab already built, so the cards and the
 * export can never be derived from different numbers.
 */
export interface VariantComparisonRow {
    __rowKey?: string
    data?: {
        split_test?: { step_id?: number | string | null; name?: string | null } | null
        variant?: { id?: number | string | null; name?: string | null; percentage?: number | null } | null
    }
    stats?: {
        entrances?: number | null
        impressions?: number | null
        clicks?: number | null
        ctr?: number | null
        purchases?: number | null
        purchaseAmount?: number | null
        purchaseRate?: number | null
    }
}

/** One revision's banner content plus the variant rows that belong to it. */
export interface VariantComparisonRevisionGroup {
    meta: RevisionMeta
    subtotals: RevisionSubtotals
    rows: VariantComparisonRow[]
}

/** The variant rows of one split test, in the order they arrived. */
export interface VariantComparisonSplitTestSection {
    /** Stable identity for the section, used as its React key. */
    key: string
    name: string
    rows: VariantComparisonRow[]
}

export interface SplitTestVariantsComparisonProps {
    /**
     * Revision groups, newest first, when the journey has more than one revision and
     * the reader is scoped to all of them. Null renders a plain run of split-test
     * sections with no revision containers.
     */
    groups: VariantComparisonRevisionGroup[] | null
    /** Every variant row in view. Empty renders the report's own empty state. */
    rows: VariantComparisonRow[]
    /** Branch key of the promoted variant, read from the journey's step configuration. */
    promotedVariantKey: string | null
    /** Whether this domain's purchase figures belong on the cards. */
    showPurchases: boolean
    /** ISO 4217 code resolved from the domain — one currency per domain. */
    currencyCode: string
    collapsedRevisions: Record<number, boolean>
    onToggleRevision: (revisionId: number) => void
    /** One-line note shown inside a revision container that contributed no rows. */
    emptyNote: string
    /** Copy for the whole-report empty state, shown when there is no data at all. */
    emptyStateText: string
    loading: boolean
}

/**
 * Partition a revision's rows into one section per split test, preserving the order
 * the rows arrived in.
 *
 * A split test is identified by the graph step that owns it, which is stable across
 * the rows of one test and distinct between two tests that happen to share a name.
 * The name is the fallback for a row reporting no step: keying on an absent id would
 * fold every split test in the revision into a single section.
 */
export function groupRowsBySplitTest(rows: VariantComparisonRow[]): VariantComparisonSplitTestSection[] {
    const sections: VariantComparisonSplitTestSection[] = []
    const sectionsByKey = new Map<string, VariantComparisonSplitTestSection>()

    for (const row of rows) {
        const splitTest = row?.data?.split_test
        const stepId = splitTest?.step_id
        const name = splitTest?.name || '—'

        // Rows carrying neither a step id nor a name still need a section rather than
        // being dropped, so an un-keyed row falls back to its name-derived bucket.
        const sectionKey = stepId != null && stepId !== '' ? `step:${stepId}` : `name:${name}`

        let section = sectionsByKey.get(sectionKey)
        if (!section) {
            section = { key: sectionKey, name, rows: [] }
            sectionsByKey.set(sectionKey, section)
            sections.push(section)
        }

        section.rows.push(row)
    }

    return sections
}

/** "1 variant" / "3 variants" — the count under a split test's name. */
function variantCountLabel(count: number): string {
    return `${count} ${count === 1 ? 'variant' : 'variants'}`
}

/**
 * The share of entrants routed to this arm. The percentage arrives as a whole number
 * (50 for half), matching what the allocation control writes into the step.
 */
function allocationLabel(percentage?: number | null): string {
    if (percentage == null) return '—'

    return `${numberWithPercent(percentage / 100)} of entrants`
}

interface MetricRowProps {
    label: string
    children: React.ReactNode
    /** Set on the figure a reader is hunting for, which is given its own weight. */
    emphasized?: boolean
}

const MetricRow: React.FC<MetricRowProps> = ({ label, children, emphasized }) => (
    <div className={emphasized ? 'stvc-metric-row stvc-metric-row-emphasized' : 'stvc-metric-row'}>
        <span className="stvc-metric-label">{label}</span>
        <span className="stvc-metric-value">{children}</span>
    </div>
)

interface VariantCardProps {
    row: VariantComparisonRow
    promotedVariantKey: string | null
    showPurchases: boolean
    currencyCode: string
}

const VariantCard: React.FC<VariantCardProps> = ({ row, promotedVariantKey, showPurchases, currencyCode }) => {
    const variant = row?.data?.variant ?? {}
    const stats = row?.stats ?? {}

    // The promoted arm is the one the journey's configuration names. It is deliberately
    // not derived from the figures on the card: a card must never be marked because it
    // happens to lead on a metric.
    const isPromoted = isPromotedVariantRow({ variant: variant as any }, promotedVariantKey)

    return (
        <div
            className={isPromoted ? 'stvc-variant-card is-promoted' : 'stvc-variant-card'}
            data-variant-key={variant?.id != null ? String(variant.id) : undefined}
        >
            <Flex align="center" gap="var(--space-2)" className="stvc-variant-name-row">
                <span className="stvc-variant-name">{variant?.name || '—'}</span>
                {isPromoted && (
                    <Tag color="success" bordered={false} className="insights-promoted-tag">
                        🏆 Promoted
                    </Tag>
                )}
            </Flex>

            <div className="stvc-variant-allocation">{allocationLabel(variant?.percentage)}</div>

            <div className="stvc-variant-metrics">
                <MetricRow label="Entrants">{numberWithCommas(stats.entrances ?? 0)}</MetricRow>
                <MetricRow label="Impressions">
                    <ImpressionsColumnView impressions={stats.impressions as number} />
                </MetricRow>
                <MetricRow label="Clicks">
                    <ClicksColumnView clicks={stats.clicks as number} ctrDecimal={stats.ctr ?? undefined} />
                </MetricRow>
                {showPurchases && (
                    <MetricRow label="Purchases">
                        <PurchasesColumnView
                            purchases={stats.purchases ?? 0}
                            purchaseRateDecimal={stats.purchaseRate}
                        />
                    </MetricRow>
                )}
                {showPurchases && (
                    <MetricRow label="Revenue" emphasized={true}>
                        <RevenueColumnView amount={stats.purchaseAmount ?? 0} currencyCode={currencyCode} />
                    </MetricRow>
                )}
            </div>
        </div>
    )
}

interface SplitTestSectionProps extends Omit<VariantCardProps, 'row'> {
    section: VariantComparisonSplitTestSection
}

const SplitTestSection: React.FC<SplitTestSectionProps> = ({ section, ...cardProps }) => (
    <section className="stvc-split-test-section" data-split-test-key={section.key}>
        <div className="stvc-split-test-eyebrow">Split Test</div>
        <div className="stvc-split-test-name">{section.name}</div>
        <div className="stvc-split-test-variant-count">{variantCountLabel(section.rows.length)}</div>

        <div className="stvc-variant-cards">
            {section.rows.map((row, index) => (
                <VariantCard key={row.__rowKey ?? `variant:${index}`} row={row} {...cardProps} />
            ))}
        </div>
    </section>
)

export const SplitTestVariantsComparison: React.FC<SplitTestVariantsComparisonProps> = ({
    groups,
    rows,
    promotedVariantKey,
    showPurchases,
    currencyCode,
    collapsedRevisions,
    onToggleRevision,
    emptyNote,
    emptyStateText,
    loading,
}) => {
    const cardProps = { promotedVariantKey, showPurchases, currencyCode }

    const renderSections = (groupRows: VariantComparisonRow[]) =>
        groupRowsBySplitTest(groupRows).map((section) => (
            <SplitTestSection key={section.key} section={section} {...cardProps} />
        ))

    let body: React.ReactNode

    if (rows.length === 0) {
        // No data for any revision reads as one message about the report, not as a
        // header-and-note for every revision the journey has ever had.
        body = (
            <div className="stvc-empty">
                <FeatureEmptyState slot="nav:notifications" text={emptyStateText} />
            </div>
        )
    } else if (groups) {
        body = groups.map((group) => {
            const collapsed = collapsedRevisions[group.meta.id] ?? false

            return (
                <div className="stvc-revision-group" key={group.meta.id} data-revision-id={group.meta.id}>
                    <div className="stvc-revision-group-banner">
                        <RevisionGroupHeader
                            meta={group.meta}
                            subtotals={group.subtotals}
                            collapsed={collapsed}
                            onToggle={() => onToggleRevision(group.meta.id)}
                            // The banner is the full width of its container here rather than
                            // a merged run of table cells, so there is nothing to span.
                            colSpan={1}
                            report="SPLIT_TEST_VARIANTS"
                            showPurchases={showPurchases}
                        />
                    </div>

                    {!collapsed && (
                        <div className="stvc-revision-group-body">
                            {group.rows.length === 0 ? (
                                <div className="revision-group-empty-note">
                                    <span className="rge-dash">—</span>
                                    <span>{emptyNote}</span>
                                </div>
                            ) : (
                                renderSections(group.rows)
                            )}
                        </div>
                    )}
                </div>
            )
        })
    } else {
        body = renderSections(rows)
    }

    return (
        <Spin spinning={loading} indicator={<LoadingOutlined spin={true} />}>
            <div className="split-test-variants-comparison">{body}</div>
        </Spin>
    )
}
